/**
 * teacherApi — typed fetchers + React Query hooks for the Teacher bucket
 * endpoints (Supplies, Resources, Open shifts, Work history, Pay). Shapes
 * mirror `backend/src/services/teacher-dashboards.types.ts`. All endpoints are
 * self-scoped on `req.user.userId` server-side; the client just hits them, no
 * query params.
 *
 * Also carries `toWorkshopChip`, the mapper from a region-flavoured chip to
 * the shared calendar's `WorkshopChip` — still used by `use-my-workshops`.
 */
import type { CrewRole } from '@bonsai/shared';
import { useQuery } from '@tanstack/react-query';

import type { CurrentRate } from '../../../api/staff-pay-rates';
import { apiFetch } from '../../../lib/apiFetch';
import type { WorkshopChip } from '../primitives';

export interface TeacherEnvelope<T> {
  mode: 'live';
  scope: { userId: string };
  data: T;
}

export interface KpiCell {
  label: string;
  value: string;
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  spark?: number[];
}

export interface TeacherWorkshopChip {
  id: string;
  /** Absolute instant (UTC ISO). Sort/compare only — never displayed (D10). */
  date: string;
  /** Venue-local wall clock (naive ISO, no `Z`) for display. Null when unset. */
  localDate: string | null;
  venueName: string;
  venueShort: string;
  venueId: string;
  capacity: number;
  booked: number;
  treeSpecies: string | null;
  leadInstructor: string | null;
  assistant: string | null;
  isMine: boolean;
  myRole: CrewRole | null;
  accentColor: string;
  subregionLabel: string;
  regionSlug: string | null;
  status: 'sold-out' | 'on-track' | 'soft' | 'open';
  replacements?: number;
}

/** Map a teacher region chip to the shared WorkshopsCalendar chip (mine-highlighted). */
export function toWorkshopChip(w: TeacherWorkshopChip): WorkshopChip {
  return {
    id: w.id,
    date: w.date,
    localDate: w.localDate,
    venueName: w.venueName,
    venueShort: w.venueShort,
    species: w.treeSpecies,
    capacity: w.capacity,
    booked: w.booked,
    leadInstructor: w.leadInstructor,
    assistant: w.assistant,
    accentColor: w.accentColor,
    subregionLabel: w.subregionLabel,
    regionSlug: w.regionSlug,
    status: w.status,
    replacements: w.replacements,
    mine: w.isMine,
    myRole: w.myRole,
  };
}

export interface TeacherSuppliesDto {
  kpis: KpiCell[];
  // Inventory-dependent surface — rendered behind a MockedOverlay until
  // the inventory module lands (BON-151).
  mocked: true;
}

export interface TeacherResourcesDto {
  /**
   * BON-96: fully-mocked design skeleton (Training & certifications, Library,
   * What's new). No live fields — the surface renders em-dash placeholders
   * behind a MockedOverlay until the content-authoring story ships (BON-66).
   */
  mocked: true;
}

/** Open shifts this teacher is eligible for (BON-242). Row shape mirrors
 *  EligibleShift in backend/src/services/shift-pickup.service.ts. */
export interface EligibleShiftEntry {
  id: string;
  workshopId: string;
  startsAtIso: string;
  venueShort: string;
  workshopTypeName: string;
  position: string;
  publishedAtIso: string;
  myRequestStatus: 'pending' | 'withdrawn' | null;
  requestCount: number;
}

/** Why eligible-looking shifts didn't reach the board — three counts because
 *  they need three different sentences (see TeacherOpenShifts' empty states). */
export interface FilteredReasons {
  noAvailabilitySet: number;
  outsideDeclaredHours: number;
  alreadyWorking: number;
}

/** An account-setup gap the teacher can't fix themselves — no region assigned,
 *  or no crew positions granted. `null` when the account is set up. */
export type EligibilityGap = 'no_regions' | 'no_positions' | null;

export interface TeacherShiftsDto {
  shifts: EligibleShiftEntry[];
  filtered: FilteredReasons;
  gap: EligibilityGap;
}

/** One workshop this teacher was crewed on (BON-244). Mirrors
 *  WorkHistoryEntry in backend/src/services/teacher-work-history.service.ts. */
export interface WorkHistoryEntry {
  workshopId: string;
  /** Absolute instant (UTC ISO). Sort/compare only — never displayed (D10). */
  startsAtIso: string;
  /** Venue-local wall clock (naive ISO, no `Z`) — the form the DATE is shown
   *  from. Null when the row has no stored local time. */
  localStartsAtIso: string | null;
  venueName: string;
  venueCity: string | null;
  workshopTypeName: string | null;
  position: string;
}

export interface TeacherHistoryDto {
  /** Worked shifts, most recent first. */
  past: WorkHistoryEntry[];
  /** Scheduled shifts, soonest first. */
  upcoming: WorkHistoryEntry[];
  /** `YYYY-MM-DD` of the earliest crew record in the SYSTEM, or null when there
   *  are none. Compared against the teacher's own earliest entry to decide
   *  whether older work may simply be unrecorded (BON-402). */
  recordsBeginIso: string | null;
}

/** One shift with its pay resolved (BON-247). Mirrors PayShiftEntry in
 *  backend/src/services/teacher-pay.service.ts. */
export interface PayShiftEntry {
  workshopId: string;
  /** Absolute instant (UTC ISO). Sort/compare only — never displayed (D10). */
  startsAtIso: string;
  /** Venue-local wall clock (naive ISO, no `Z`) — the form the shift DATE is
   *  shown from. Null when unset. Distinct from the pay-calendar dates. */
  localStartsAtIso: string | null;
  venueName: string;
  workshopTypeName: string | null;
  position: string;
  rateKey: string | null;
  /** Whether the person's own rate or the company default priced it; null when unpriced. */
  rateSource: 'personal' | 'default' | null;
  /** Null when unpriced — never 0, which would read as unpaid work. */
  amountCents: number | null;
  worked: boolean;
}

export interface PayGroup {
  payDateIso: string;
  coversFromIso: string;
  coversToIso: string;
  /** Null when any shift in the group is unpriced. */
  totalCents: number | null;
  isPast: boolean;
  shifts: PayShiftEntry[];
}

export interface TeacherPayDto {
  groups: PayGroup[];
  /** Shifts outside every configured pay period — no pay date yet. */
  unscheduled: PayShiftEntry[];
  calendarConfigured: boolean;
  /** Paid a salary, so no shift carries a per-shift amount. */
  salaried: boolean;
  /** The teacher's rate today for each role, in the fixed role order. */
  myRates: CurrentRate[];
}

export type TeacherBucket = 'supplies' | 'resources' | 'shifts' | 'history' | 'pay';

async function fetchBucket<T>(bucket: TeacherBucket): Promise<TeacherEnvelope<T>> {
  const res = await apiFetch(`/api/v1/dashboards/teacher/${bucket}`);
  if (!res.ok) {
    throw new Error(`Failed to load ${bucket}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as TeacherEnvelope<T>;
}

const STALE_30S = 30_000;

export function useTeacherShifts() {
  return useQuery({
    queryKey: ['dashboards.teacher', 'shifts'],
    queryFn: () => fetchBucket<TeacherShiftsDto>('shifts'),
    staleTime: STALE_30S,
  });
}

export function useTeacherHistory() {
  return useQuery({
    queryKey: ['dashboards.teacher', 'history'],
    queryFn: () => fetchBucket<TeacherHistoryDto>('history'),
    staleTime: STALE_30S,
  });
}

export function useTeacherPay() {
  return useQuery({
    queryKey: ['dashboards.teacher', 'pay'],
    queryFn: () => fetchBucket<TeacherPayDto>('pay'),
    staleTime: STALE_30S,
  });
}

export function useTeacherSupplies() {
  return useQuery({
    queryKey: ['dashboards.teacher', 'supplies'],
    queryFn: () => fetchBucket<TeacherSuppliesDto>('supplies'),
    staleTime: STALE_30S,
  });
}

export function useTeacherResources() {
  return useQuery({
    queryKey: ['dashboards.teacher', 'resources'],
    queryFn: () => fetchBucket<TeacherResourcesDto>('resources'),
    staleTime: STALE_30S,
  });
}
